import { Inject, Injectable } from '@angular/core';
import { delay, map, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { add } from 'date-fns';
import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import { FEATURE_FLAG_SERVICE, FeatureFlagKey, IFeatureFlagService } from '@sondermind/data-access/feature-flags';
import { SessionType } from '@sondermind/utilities/models-session';
import {
  AvailabilityOptions,
  AvailabilityResponseFormat,
  CoreAvailabilityOptions,
  CoreParticipantMember,
  ParticipantMember,
  ParticipantsRequired,
  ProviderAvailability,
  ProviderAvailabilityMember,
} from '../models/availability-dtos';
import { SchedulingServiceSessionType } from '../models/scheduling-service-session-type.enum';
import {
  ICoreAvailabilityApiResponse,
  mapCoreAvailabilityToApiResponse
} from '../models/core-availability-response';
import { IAvailabilityApiResponse } from '../models/availability-response';
import { buildMockSlots } from './scheduling-availability-local-mock';

@Injectable({
  providedIn: 'root'
})
export class SchedulingAvailabilityService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
    private logger: Logger,
    @Inject(FEATURE_FLAG_SERVICE)
    private featureFlagService: IFeatureFlagService,
  ) { }

  private getPublicAvailability(opts: AvailabilityOptions): Observable<ProviderAvailability> {
    const url = `${this.configurationService.env.schedulingSvcBase}/public/v1/availabilities`;

    return this.http.post<IAvailabilityApiResponse>(url, opts).pipe(
      map((res: IAvailabilityApiResponse) => new ProviderAvailability(res)),
      catchError((error: unknown) => {
        this.logError(error, url, opts);
        return throwError(() => error);
      })
    );
  }

  private getAvailability(opts: AvailabilityOptions): Observable<ProviderAvailability> {
    const url = `${this.configurationService.env.schedulingSvcBase}/api/v2/providers/availability`;

    return this.http.post<IAvailabilityApiResponse>(url, opts).pipe(
      map((res: IAvailabilityApiResponse) => new ProviderAvailability(res)),
      catchError((error: unknown) => {
        this.logError(error, url, opts);
        return throwError(() => error);
      })
    );
  }

  private getCoreAvailability(opts: CoreAvailabilityOptions): Observable<ProviderAvailability> {
    const url = `${this.configurationService.env.schedulingCoreSvcBase}/api/v1/providers/availability`;

    return this.http.post<ICoreAvailabilityApiResponse>(url, opts).pipe(
      map((res) => new ProviderAvailability(mapCoreAvailabilityToApiResponse(res))),
      catchError((error: unknown) => {
        this.logError(error, url, opts);
        return throwError(() => error);
      })
    );
  }

  private logError(error: unknown, url: string, requestBody: AvailabilityOptions | CoreAvailabilityOptions): void {
    const logContext: Record<string, unknown> = {
      url,
      requestBody
    };

    if (error instanceof HttpErrorResponse) {
      logContext['status'] = error.status;
      logContext['statusText'] = error.statusText;
      logContext['error'] = error.error;
      this.logger.error(`Providers availability endpoint error: ${error.status}`, logContext);
    } else {
      logContext['error'] = error;
      this.logger.error('Providers availability endpoint error', logContext);
    }
  }

  private buildAvailabilityOptions(
    providerMembers: ProviderAvailabilityMember[],
    start: Date,
    end: Date,
    slotDuration: number,
    sessionType?: SchedulingServiceSessionType
  ): AvailabilityOptions {
    const members: ParticipantMember[] = providerMembers.map((member) => {
      const apiMember: ParticipantMember = {
        provider_id: member.providerId
      };
      if (member?.providerRelationship) {
        apiMember.provider_relationship = member.providerRelationship;
      }
      return apiMember;
    });

    return {
      participants: [{
        members,
        required: ParticipantsRequired.ANY,
      }],
      required_duration: {
        minutes: slotDuration,
      },
      available_periods: [{
        start: start.toISOString(),
        end: end.toISOString(),
      }],
      response_format: AvailabilityResponseFormat.SLOTS,
      ...sessionType && { session_type: sessionType },
    };
  }

  private buildCoreAvailabilityOptions(
    providerMembers: ProviderAvailabilityMember[],
    start: Date,
    end: Date,
    sessionType?: SchedulingServiceSessionType,
    requiredDurationMinutes?: number
  ): CoreAvailabilityOptions {
    const members: CoreParticipantMember[] = providerMembers.map((member) => {
      const apiMember: CoreParticipantMember = {
        providerId: member.providerId.toString(),
      };
      if (member?.providerRelationship) {
        apiMember.providerRelationship = member.providerRelationship;
      }
      return apiMember;
    });

    const base: CoreAvailabilityOptions = {
      participants: [{
        members,
        required: ParticipantsRequired.ANY,
      }],
      availablePeriods: [{
        start: start.toISOString(),
        end: end.toISOString(),
      }],
      responseFormat: AvailabilityResponseFormat.SLOTS,
      sessionType,
    };

    if (requiredDurationMinutes != null && requiredDurationMinutes > 0) {
      return {
        ...base,
        requiredDuration: { minutes: requiredDurationMinutes },
      };
    }

    return base;
  }

  getAvailabilityForPublicProviders(
    providerIds: number[],
    start: Date = add(new Date(), { days: 2 }),
    end: Date = add(new Date(), { days: 9 }),
    slotDuration: number = 60
  ): Observable<ProviderAvailability> {
    if (!providerIds || providerIds.length === 0) {
      this.logger.warn('getAvailabilityForPublicProviders called with empty providerIds array');
      return of(new ProviderAvailability({ available_slots: [], cache: false, cache_timestamp: new Date() }));
    }
    // Commented out to allow real API calls to localhost:3010
    // if (this.configurationService.env?.schedulingSvcBase?.includes('localhost')) {
    //   return this.getMockSlotsForLocalhost(providerIds, start, end, 'America/Denver', slotDuration);
    // }
    const providerMembers: ProviderAvailabilityMember[] = providerIds.map((id) => ({ providerId: id }));
    const options = this.buildAvailabilityOptions(providerMembers, start, end, slotDuration);
    return this.getPublicAvailability(options);
  }

  getAvailabilityForProviders(
    providerMembers: ProviderAvailabilityMember[],
    start: Date = add(new Date(), { days: 2 }),
    end: Date = add(new Date(), { days: 9 }),
    sessionType?: SessionType,
    slotDuration: number = 60,
    requiredDurationMinutes?: number,
  ): Observable<ProviderAvailability> {
    if (!providerMembers || providerMembers.length === 0) {
      this.logger.warn('getAvailabilityForProviders called with empty providerMembers array');
      return of(new ProviderAvailability({ available_slots: [], cache: false, cache_timestamp: new Date() }));
    }
    // Commented out to allow real API calls to localhost:3010
    // if (this.configurationService.env?.schedulingSvcBase?.includes('localhost')) {
    //   const providerIds = providerMembers.map((m) => m.providerId);
    //   return this.getMockSlotsForLocalhost(providerIds, start, end, 'America/Denver', slotDuration);
    // }

    return this.featureFlagService.isEnabled$(FeatureFlagKey.VARIABLE_SESSION_DURATION).pipe(
      take(1),
      switchMap((isEnabled) => {
        if (isEnabled) {
          const convertedSessionType = this.convertSessionTypeToSchedulingServiceSessionType(sessionType);
          const options = this.buildCoreAvailabilityOptions(
            providerMembers,
            start,
            end,
            convertedSessionType,
            requiredDurationMinutes,
          );
          return this.getCoreAvailability(options);
        }
        const effectiveSlotMinutes = requiredDurationMinutes ?? slotDuration;
        const options = this.buildAvailabilityOptions(
          providerMembers,
          start,
          end,
          effectiveSlotMinutes,
          SchedulingServiceSessionType.HYBRID,
        );
        return this.getAvailability(options);
      })
    );
  }

  private convertSessionTypeToSchedulingServiceSessionType(type: SessionType): SchedulingServiceSessionType {
    switch (type) {
      case SessionType.IN_PERSON: {
        return SchedulingServiceSessionType.IN_PERSON;
      }
      case SessionType.VIDEO: {
        return SchedulingServiceSessionType.VIRTUAL;
      }
      case SessionType.NO_PREFERENCE:
      default: {
        return SchedulingServiceSessionType.HYBRID;
      }
    }
  }

  private getMockSlotsForLocalhost(providerIds: number[], start: Date, end: Date, timeZone: string = 'America/Denver', slotDuration: number) {
    const availability_slots = buildMockSlots(start, end, timeZone, slotDuration, providerIds);
    const mock: IAvailabilityApiResponse = {
      available_slots: availability_slots,
      cache: true,
      cache_timestamp: new Date(),
    };
    return of(new ProviderAvailability(mock)).pipe(delay(500));
  }
}
